import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, message, Row } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type BmwFormValues } from '.';
import { type BmwModuleSpecsFragment } from '../../../../api/fragments/BmwModuleSpecs';
import {
    UpdateBmwModuleDocument,
    type UpdateBmwModuleMutation,
    type UpdateBmwModuleMutationVariables,
} from '../../../../api/mutations/updateBmwModule';
import { BankIntegrationProvider, ModuleType } from '../../../../api/types';
import BankSelectField from '../../../../components/fields/BankSelectField';
import DealerSelectField from '../../../../components/fields/DealerSelectField';
import Form from '../../../../components/fields/Form';
import InputField from '../../../../components/fields/InputField';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import useModuleOptions from '../modules/implementations/shared/useModuleOptions';

const validator = validators.compose(
    validators.requiredString('customerModuleId'),
    validators.requiredString('vehicleModuleId'),
    validators.requiredString('bankId'),
    validators.requiredString('sdmHost'),
    validators.requiredString('sdmAccessKey'),
    validators.requiredString('dealerId')
);

type BmwModuleFormProps = {
    module: BmwModuleSpecsFragment;
};

const BmwModuleForm = ({ module }: BmwModuleFormProps) => {
    const { t } = useTranslation(['bmwModuleDetails', 'moduleDetails']);
    const options = useModuleOptions(module.company);
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);

    const initialValues: BmwFormValues = useMemo(
        () => ({
            dealerId: module.dealerId,
            bankId: module.bankId,
            vehicleModuleId: module.optionalVehicleModuleId,
            customerModuleId: module.customerModuleId,
            sdmHost: module.bmwSetting.sdmHost,
            sdmAccessKey: module.bmwSetting.sdmAccessKey,
        }),
        [module]
    );

    const onSubmit = useHandleError(
        async (values: BmwFormValues) => {
            message.loading({
                content: t('bmwModuleDetails:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateBmwModuleMutation, UpdateBmwModuleMutationVariables>({
                    mutation: UpdateBmwModuleDocument,
                    variables: { moduleId: module.id, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('bmwModuleDetails:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateForm" name="updateForm" onSubmitCapture={handleSubmit}>
                    <Card
                        extra={
                            <Button form="updateForm" htmlType="submit" type="primary">
                                {t('bmwModuleDetails:actions.update')}
                            </Button>
                        }
                        title={t('bmwModuleDetails:tabs.configuration.title')}
                    >
                        <Row gutter={10}>
                            <Col md={8} xs={24}>
                                <SelectField
                                    {...t('moduleDetails:fields.customerModuleId', { returnObjects: true })}
                                    name="customerModuleId"
                                    options={options.customers}
                                    required
                                    showSearch
                                />
                            </Col>
                            {!module.optionalVehicleModuleId && (
                                <Col md={8} xs={24}>
                                    <ModuleSelectField
                                        {...t('moduleDetails:fields.vehicleModuleId', { returnObjects: true })}
                                        companyId={module.company.id}
                                        moduleType={ModuleType.SimpleVehicleManagement}
                                        name="vehicleModuleId"
                                        required
                                    />
                                </Col>
                            )}
                            <Col md={8} xs={24}>
                                <BankSelectField
                                    {...t('moduleDetails:fields.bankId', { returnObjects: true })}
                                    bankIntegrationProviders={[
                                        BankIntegrationProvider.Email,
                                        BankIntegrationProvider.Dbs,
                                    ]}
                                    companyId={module.company.id}
                                    moduleId={module.bankModule.id}
                                    name="bankId"
                                    required
                                />
                            </Col>
                            <Col md={8} xs={24}>
                                <DealerSelectField
                                    {...t('moduleDetails:fields.dealer', { returnObjects: true })}
                                    companyId={module.company.id}
                                    forceSkipFetch={!module.company.id}
                                    name="dealerId"
                                    required
                                />
                            </Col>
                            <Col md={8} xs={24}>
                                <InputField
                                    {...t('bmwModuleDetails:fields.sdmHost', { returnObjects: true })}
                                    addonBefore="https://"
                                    name="sdmHost"
                                    required
                                />
                            </Col>
                            <Col md={8} xs={24}>
                                <InputField
                                    {...t('bmwModuleDetails:fields.sdmAccessKey', { returnObjects: true })}
                                    name="sdmAccessKey"
                                    required
                                />
                            </Col>
                            <Col md={8} xs={24}>
                                <PasswordField
                                    {...t('bmwModuleDetails:fields.sdmSecretKey', { returnObjects: true })}
                                    name="sdmSecretKey"
                                    placeholder="•••••"
                                />
                            </Col>
                        </Row>
                    </Card>
                </Form>
            )}
        </Formik>
    );
};

export default BmwModuleForm;
